import { GAP } from 'lib/helpers/exportPdf/lib/constants';

export const lineHeight = (GAP / 2) * 0.8;
export const smallSpacing = 2;

export const addPageIfNeeded = (doc, startY) => {
  const pageHeight = doc.internal.pageSize.height;
  const minBottomMargin = 30; // footer height

  if (startY > pageHeight - minBottomMargin) {
    doc.addPage();
    return GAP;
  }

  return startY;
};

export const addBackgroundColor = (doc, startY, pageWidth, height = lineHeight) => {
  doc.setFillColor(245, 245, 245);
  doc.rect(0, startY - lineHeight, pageWidth, height + 1, 'F');
};

export const addNewLineIfNeeded = (doc, startY, currentX, currValue) => {
  const pageWidth = doc.internal.pageSize.width;

  const toWidth = smallSpacing + doc.getTextWidth('to');
  const valueWidth = doc.getTextWidth(currValue);
  const currentValueBlockWidth = smallSpacing + valueWidth;
  const requiredWidth = toWidth + currentValueBlockWidth;
  const remainingWidth = pageWidth - currentX - GAP;

  if (requiredWidth > remainingWidth) {
    startY += lineHeight;
    currentX = false; // reset currentX
  }

  return { newStartY: startY, newCurrentX: currentX };
};

// Helper to wrap text, draw it, and update Y position
export const drawWrappedText = (doc, text, currentX, startY) => {
  const pageWidth = doc.internal.pageSize.width;

  const usableWidth = pageWidth - currentX - GAP;
  const wrappedText = doc.splitTextToSize(text, usableWidth);
  doc.text(wrappedText, currentX + 1, startY);

  const hasMultipleLines = wrappedText.length > 1;

  // Calculate the height of the wrapped text
  let newY = startY;

  if (hasMultipleLines) {
    const linesHeight = wrappedText.length * lineHeight;
    newY = startY + linesHeight;
  }

  const newCurrentX = hasMultipleLines ? GAP : currentX + doc.getTextWidth(text);

  return { newY, newCurrentX };
};

// Draw a vertical line between two Y positions
export const drawVerticalLine = (doc, x, startY, endY, options = {}) => {
  if (endY <= startY) {
    return;
  }

  const { color = 200, lineWidth = 0.2 } = options;

  doc.setDrawColor(color);
  doc.setLineWidth(lineWidth);
  doc.line(x, startY, x, endY);
};

// AVATAR UTILS
// -------------------------------------------------------------------------------------------------

export const addAvatar = async (doc, startX, startY, avatarImage, avatarInitials) => {
  if (!avatarImage) {
    return drawInitialsAvatar(doc, startX, startY, avatarInitials);
  }

  try {
    const avatarMarginRight = 1;
    const avatarSize = 4;
    const avatarTopY = startY - avatarSize + 1;

    // Upscale for better quality, use small border radius
    const scale = 20;
    const borderRadius = 2 * scale; // Small radius (0.5mm in PDF units)
    const roundedImage = await createRoundedImage(avatarImage, avatarSize * scale, borderRadius);

    if (roundedImage) {
      doc.addImage(roundedImage, 'PNG', startX, avatarTopY, avatarSize, avatarSize);
      return avatarSize + avatarMarginRight;
    }
  } catch (error) {
    console.warn('Failed to load avatar image for activity PDF, falling back to initials.', error);
  }
};

const createRoundedImage = (imageSrc, size, borderRadius) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      // Create rounded rectangle clipping path
      ctx.beginPath();
      ctx.moveTo(borderRadius, 0);
      ctx.lineTo(size - borderRadius, 0);
      ctx.quadraticCurveTo(size, 0, size, borderRadius);
      ctx.lineTo(size, size - borderRadius);
      ctx.quadraticCurveTo(size, size, size - borderRadius, size);
      ctx.lineTo(borderRadius, size);
      ctx.quadraticCurveTo(0, size, 0, size - borderRadius);
      ctx.lineTo(0, borderRadius);
      ctx.quadraticCurveTo(0, 0, borderRadius, 0);
      ctx.closePath();
      ctx.clip();

      // Draw the image
      ctx.drawImage(img, 0, 0, size, size);

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => resolve(null);
    img.src = imageSrc;
  });
};

const drawInitialsAvatar = (doc, startX, startY, initials) => {
  // Draw rounded square avatar
  const size = 5; // Same as circle diameter (2.5 * 2)
  const radius = 0.8; // Border radius for rounded corners
  const centerX = startX + smallSpacing;
  const centerY = startY - 1;

  doc.setFillColor(220, 220, 220); // light gray
  doc.roundedRect(centerX - size / 2, centerY - size / 2, size, size, radius, radius, 'F');

  // Initials text
  doc.setFontSize(5);
  doc.setTextColor(60, 60, 60);
  doc.text(initials, centerX, startY - 0.5, { align: 'center' });
  doc.setFontSize(9);

  return 6;
};
